import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Calendar } from '../components/ui/calendar';
import { Clock } from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Therapist } from '../types/user';
import { fetchAvailableSlots, bookAppointment, Appointment } from '../api/appointment';
import { formatDateToFrench } from '../utils/date';
import { ConfirmationModal } from './confirmation-modal';

interface AppointmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  therapist: Therapist;
}

const AppointmentModal = ({ isOpen, onClose, therapist }: AppointmentModalProps) => {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedTime, setSelectedTime] = useState<string | undefined>(undefined);
  const [stage, setStage] = useState<'date' | 'time' | 'confirm'>('date');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [timeSlots, setTimeSlots] = useState<Array<{ time: string; available: boolean }>>([]);
  const [confirmedAppointment, setConfirmedAppointment] = useState<Appointment | null>(null);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedDate(undefined);
      setSelectedTime(undefined);
      setStage('date');
      setError(null);
      setTimeSlots([]);
      setConfirmedAppointment(null);
    }
  }, [isOpen]);

  const handleClose = () => {
    onClose();
    setSelectedDate(undefined);
    setSelectedTime(undefined);
    setStage('date');
    setError(null);
    setTimeSlots([]);
    setConfirmedAppointment(null);
  };

  if (!isOpen) return null;

  const handleDateSelect = async (date: Date | undefined) => {
    setSelectedDate(date);
    setError(null);
    
    if (date) {
      setIsLoading(true);
      try {
        const slots = await fetchAvailableSlots(therapist.id, date);
        setTimeSlots(slots);
        setStage('time');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erreur lors du chargement des disponibilités');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleTimeSelect = (time: string) => {
    setSelectedTime(time);
    setError(null);
    setStage('confirm');
  };

  const handleConfirm = async () => {
    if (!selectedDate || !selectedTime) return;

    try {
      setIsLoading(true);
      setError(null);
      const appointment = await bookAppointment(therapist.id, selectedDate, selectedTime);
      setConfirmedAppointment(appointment);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la réservation');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">
            Rendez-vous avec {therapist.name}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700"
          >
            ×
          </button>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {stage === 'date' && (
          <div className="space-y-4">
            <p className="text-gray-600">
              Sélectionnez une date pour votre consultation
            </p>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={handleDateSelect}
              className="rounded-md border"
              therapistId={therapist.id}
              disabled={(date: Date) => {
                // Past dates
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                if (date < today) return true;

                // Weekends
                return date.getDay() === 0 || date.getDay() === 6;
              }}
            />
          </div>
        )}

        {stage === 'time' && selectedDate && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-gray-600 mb-4">
              <Clock size={20} />
              <span>
                {formatDateToFrench(selectedDate.toString())}
              </span>
            </div>
            
            {isLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#00c4cc]"></div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {timeSlots.map((slot) => (
                  <Button
                    key={slot.time}
                    onClick={() => handleTimeSelect(slot.time)}
                    disabled={!slot.available}
                    variant={slot.available ? 'outline' : 'ghost'}
                    className={`h-12 ${
                      !slot.available && 'text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    {slot.time}
                  </Button>
                ))}
              </div>
            )}
            
            <Button
              onClick={() => setStage('date')}
              variant="ghost"
              className="mt-4"
            >
              Changer la date
            </Button>
          </div>
        )}

        {stage === 'confirm' && selectedDate && selectedTime && (
          <div className="space-y-6">
            <div className="bg-gray-50 p-4 rounded-lg space-y-3">
              <h3 className="font-medium">Résumé de votre rendez-vous</h3>
              <div className="space-y-2 text-gray-600">
                <p>📅 {formatDateToFrench(selectedDate)}</p>
                <p>🕒 {selectedTime}</p>
                <p>👩‍⚕️ {therapist.name}</p>
              </div>
            </div>
            
            <div className="space-y-3">
              <Button
                onClick={handleConfirm}
                className="w-full text-white"
                style={{backgroundColor: '#00c4cc'}}
                disabled={isLoading}
              >
                {isLoading ? 'Confirmation en cours...' : 'Confirmer le rendez-vous'}
              </Button>
              <Button
                onClick={() => setStage('time')}
                variant="ghost"
                className="w-full"
                disabled={isLoading}
              >
                Modifier l'horaire
              </Button>
            </div>
          </div>
        )}
      </div>

      {confirmedAppointment && (
        <ConfirmationModal
          isOpen={!!confirmedAppointment}
          onClose={() => {
            setConfirmedAppointment(null);
            handleClose();
          }}
          appointment={confirmedAppointment}
        />
      )}
    </div>
  );
};

export { AppointmentModal };